<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url(' + bg + ')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">Portfolio</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4">
                    <li><router-link to="/">Home</router-link></li>
                    <li>/</li>
                    <li><router-link to="/portfolio-v1">Portfolio</router-link></li>
                    <li>/</li>
                    <li class="text-primary">{{ data ? data.title : 'Portfolio' }}</li>
                </ul>
            </div>
        </div>

        <div class="portfolio-single s-py-100">
            <div class="container-fluid">
                <p v-if="loading" class="text-center text-paragraph dark:text-white-light">Loading...</p>
                <p v-else-if="error" class="text-center text-red-500">{{ error }}</p>
                <div v-else-if="data" class="flex flex-col-reverse md:flex-row gap-8 lg:gap-16 justify-between">
                    <div class="w-full flex-1 md:max-w-[720px] lg:max-w-[820px]">
                        <div class="relative mx-auto w-full max-w-[820px] overflow-hidden rounded-[8px] bg-[#F6F2EC] aspect-[4/3] sm:aspect-[16/11] lg:aspect-[16/10]">
                            <img class="absolute inset-0 h-full w-full object-cover" :src="selectedImage" alt="Portfolio">
                        </div>
                        <div v-if="secondaryImages.length" class="mt-5 flex flex-wrap gap-3">
                            <button
                                v-for="(img, index) in secondaryImages"
                                :key="index"
                                type="button"
                                class="h-[120px] w-[120px] shrink-0 overflow-hidden rounded-[8px] bg-[#F6F2EC]"
                                :class="selectedImage === img ? 'ring-2 ring-primary ring-offset-2 ring-offset-white dark:ring-offset-title' : 'opacity-80 hover:opacity-100 transition duration-300'"
                                @click="selectedImage = img"
                            >
                                <img class="h-full w-full object-cover" :src="img" alt="Portfolio gallery image">
                            </button>
                        </div>
                    </div>

                    <div class="xl:max-w-[560px] lg:max-w-[420px] md:max-w-[330px] max-w-[450px] w-full lg:h-full lg:sticky lg:top-7">
                        <div>
                            <div>
                                <span v-if="data.category" class="inline-block text-title font-medium text-[15px] leading-none p-[10px] rounded-md bg-primary-midum">
                                    {{ data.category }}
                                </span>
                                <h2 class="font-semibold leading-none mt-4 md:mt-6 text-2xl md:text-3xl">
                                    {{ data.title }}
                                </h2>
                                <p v-if="data.description || data.short_description" class="mt-3 text-base sm:text-lg whitespace-pre-line">
                                    {{ data.description || data.short_description }}
                                </p>
                            </div>

                            <table v-if="hasMeta" class="meta-table mt-[30px] sm:mt-[50px] block bg-[#FAFAFA] dark:bg-dark-secondary p-[30px]">
                                <tbody class="w-full block">
                                    <tr v-if="data.client_name" class="border-b border-black/10 dark:border-bdr-clr-drk py-4">
                                        <th class="text-[18px] sm:text-[20px] font-medium">Client</th>
                                        <td class='text-base sm:text-lg'>{{ data.client_name }}</td>
                                    </tr>
                                    <tr v-if="data.location" class="border-b border-black/10 dark:border-bdr-clr-drk py-4">
                                        <th>Location</th>
                                        <td>{{ data.location }}</td>
                                    </tr>
                                    <tr v-if="formattedCompletionDate" class="border-b border-black/10 dark:border-bdr-clr-drk py-4">
                                        <th>Completed</th>
                                        <td>{{ formattedCompletionDate }}</td>
                                    </tr>
                                    <tr v-if="data.category" class="py-4">
                                        <th>Category</th>
                                        <td>{{ data.category }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="mt-5 sm:mt-7 lg:mt-10">
                            <div class="flex items-center justify-start gap-6">
                                <h6 class="font-normal">Share : </h6>
                                <!-- Real share targets. Instagram and TikTok have no web
                                     share intent, so they belong in the footer as profile
                                     links, not here. -->
                                <div class="flex items-center gap-6">
                                    <a :href="shareLinks.whatsapp" target="_blank" rel="noopener" title="Share on WhatsApp" aria-label="Share on WhatsApp" class="text-title duration-300 dark:text-white hover:text-primary dark:hover:text-primary"><i class="fa-brands fa-whatsapp"></i></a>
                                    <a :href="shareLinks.facebook" target="_blank" rel="noopener" title="Share on Facebook" aria-label="Share on Facebook" class="text-title duration-300 dark:text-white hover:text-primary dark:hover:text-primary"><i class="fa-brands fa-facebook-f"></i></a>
                                    <a :href="shareLinks.email" title="Share by email" aria-label="Share by email" class="text-title duration-300 dark:text-white hover:text-primary dark:hover:text-primary"><i class="fa-solid fa-envelope"></i></a>
                                    <button type="button" title="Copy link" aria-label="Copy link" class="text-title duration-300 dark:text-white hover:text-primary dark:hover:text-primary" @click="copyLink"><i class="fa-solid fa-link"></i></button>
                                    <span v-if="copied" class="text-sm text-primary">Link copied</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <p v-else class="text-center text-paragraph dark:text-white-light">Portfolio not found.</p>
            </div>
        </div>

        <FooterOne/>

        <ScrollToTop/>

    </div>
</template>

<script setup>
    import { computed, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';

    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';

    import bg from '@/assets/img/shortcode/breadcumb.jpg'
    import portfolioFallback from '@/assets/img/gallery/shop-01/shop-01.jpg'
    import Aos from 'aos';
    import { storefrontRequest } from '@/lib/storefront-api';
    import { setSeo } from '@/lib/seo';

    const route = useRoute();
    const data = ref(null);
    const loading = ref(true);
    const error = ref('');
    const selectedImage = ref('');

    const galleryImages = computed(() => {
        if (!data.value) return [];
        const items = [];
        if (data.value.main_image_url) items.push(data.value.main_image_url);
        (data.value.gallery_image_urls || []).forEach((url) => {
            if (url && !items.includes(url)) items.push(url);
        });
        return items;
    });

    const secondaryImages = computed(() => galleryImages.value.slice(1));

    const hasMeta = computed(() => data.value && (
        data.value.client_name || data.value.location || data.value.completion_date || data.value.category
    ));

    const formattedCompletionDate = computed(() => {
        if (!data.value?.completion_date) return '';
        try {
            return new Date(data.value.completion_date).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric',
            });
        } catch (e) {
            return data.value.completion_date;
        }
    });

    async function loadPortfolio(id) {
        loading.value = true;
        error.value = '';
        data.value = null;
        try {
            const response = await storefrontRequest(`/portfolios/${id}`);
            data.value = response.data;
            setSeo({
                title: response.data?.title,
                description: response.data?.short_description,
                image: response.data?.main_image_url,
                path: `/portfolio-details-v1/${id}`,
                type: 'article',
            });
            selectedImage.value = response.data?.main_image_url
                || response.data?.gallery_image_urls?.[0]
                || portfolioFallback;
        } catch (e) {
            error.value = e.message || 'Failed to load portfolio.';
        } finally {
            loading.value = false;
        }
    }

    watch(() => route.params.id, (id) => { if (id) loadPortfolio(id); });

    onMounted(() => {
        Aos.init();
        if (route.params.id) loadPortfolio(route.params.id);
    });
    // Mirrors the product page's share block so the two behave identically.
    const copied = ref(false);

    const shareLinks = computed(() => {
        const url = typeof window !== 'undefined' ? window.location.href : '';
        const title = data.value?.title || 'Sweet Home Gallery';
        const message = `${title} — ${url}`;

        return {
            whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(message)}`,
        };
    });

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            copied.value = true;
            setTimeout(() => { copied.value = false; }, 2000);
        } catch (error) {
            /* clipboard blocked (insecure context or denied) — the other targets still work */
        }
    }

</script>
